import React, { useState, useEffect } from 'react'
import styled from 'styled-components'

import { getUrl } from '../firebase/storage'

import Menu from './Menu'
import StoreCode from './StoreCode'
import OffsetControls from './OffsetControls'
import { Button } from './UI'
import icons from '../assets/icons'

import { getParam } from '../utility'
import { IGlasses, IOffset } from '../store'

import { useStore, useStoreCode, useOffset } from '../hooks'

import Loading from './Loading'

const SLayout = styled.div`
    position: absolute;
    height: 100%;
    width: 100%;

    display: grid;

    grid-template-columns: auto 15rem;

    @media only screen and (max-width: ${p => p.theme.mobileWidth}) {
        grid-template-columns: 1fr;
        grid-template-rows: auto 13rem;
    }
`

const SFrame = styled.div`
    box-sizing: border-box;
    height: 100%;
    * {
        overflow: hidden;
        box-sizing: border-box;
        height: 100%;
        width: 100%;
        position: relative;
    }
`

const SFrameHolder = styled.div`
	position: relative;
`

const SRefresh = styled.div`
    position: absolute;
    bottom: 1rem;
    left: 1rem;
    @media only screen and (max-width: ${p => p.theme.mobileWidth}) {
        bottom: 11rem;
        display: none;
    }
`

interface IState {
	selectedSize: IGlasses,
	selectedSizeKey: string,
	selectedGlasses: string,
	refresh: boolean,
	loading: boolean,
	smallLoading: boolean,
    init: boolean,
}

export default function Layout() {
	const store = useStore()
	const storeCode = useStoreCode()
	const offset = useOffset()
	const [state, set] = useState<IState>({
		selectedSizeKey: getParam('model'),
		selectedSize: store?.state?.glasses,
		selectedGlasses: '0',
		refresh: false,
		loading: true,
		smallLoading: false,
        init: false,
	})

	function setLoading(value: boolean): void {
		set(prev => { return { ...prev, loading: value } })
	}

	function setSmallLoading(value: boolean): void {
		set(prev => { return { ...prev, smallLoading: value } })
	}

	function selectGlasses(glasses: string): void {
		set(prev => { return { ...prev, selectedGlasses: glasses } })
	}

	function refresh(): void {
		set(prev => { return { ...prev, refresh: true, loading: true } })
	}

	function sendMessage(type: string, payload: any): void {
		const data = { type: type, payload: payload }
		window.frames[0].postMessage(data, `${window.frames[0].location.href}/canvas`)
	}

	async function sendGlassesToFaceTracker() {
		try {
			// Set offset glasses too.
			offset.selectGlasses(state.selectedSize, state.selectedSizeKey)

			const url = await getUrl(`${state.selectedSizeKey}/glb/${state.selectedSize.Styles[state.selectedGlasses].InternalName}.glb`)
			const payload = { url }

			sendMessage('setGlasses', payload)
		} catch (error) { console.log(error) }
	}

	function sendOffsetToFaceTracker() {
		if (!state?.selectedSize) return

		let base: IOffset = {
			RotationX: 0,
			PositionY: 0,
			PositionZ: 0,
		}

		if (state?.selectedSize?.WebOffset) base = state.selectedSize.WebOffset

		// Local offset (admin config)
		const localOffset: IOffset = {
			RotationX: (base.RotationX ?? 0) + offset.state.localOffset.RotationX,
			PositionY: (base.PositionY ?? 0) + offset.state.localOffset.PositionY,
			PositionZ: (base.PositionZ ?? 0) + offset.state.localOffset.PositionZ,
		}

		sendMessage('setOffset', localOffset)
	}

	useEffect(() => {
		sendOffsetToFaceTracker()
	}, [offset.state?.localOffset])

	useEffect(() => {
		if (!state.selectedSize || !state.init) return

		sendGlassesToFaceTracker()
	}, [state.selectedGlasses, state.selectedSize])

	useEffect(() => {
		if (state.refresh) set(prev => { return { ...prev, refresh: false } })
	}, [state.refresh])

	useEffect(() => {
		if (!state.selectedSize) return

		function messageHandler(event) {
			try {
				const data = event.data

				if (data?.type === 'init') {
                    set(p => ({...p, init: true}))
					sendGlassesToFaceTracker()
					sendOffsetToFaceTracker()
				}
				if (data?.type === 'setLoading') setLoading(data.payload)
				if (data?.type === 'setSmallLoading') setSmallLoading(data.payload)

			} catch { }
		}

		window.addEventListener("message", messageHandler)

		return () => window.removeEventListener("message", messageHandler)

	}, [state.selectedSize, state.selectedGlasses])

	useEffect(() => {
		set(p => ({ ...p, selectedSize: store?.state?.glasses }))
	}, [store])

	function shouldShowLoading(): boolean {
		if (storeCode.state.hasValidCode) return (state.loading || state.smallLoading)
		else return storeCode.state.loading
	}

	return (
		<SLayout>
			<SFrameHolder>
				{
					storeCode.state.hasValidCode ?
						<SFrame>
							{!state.refresh && <iframe frameBorder='0' src='./canvas' />}
						</SFrame> :
						<StoreCode />
				}

				<OffsetControls />
			</SFrameHolder>

			<Menu
				selected={state.selectedGlasses}
				selectGlasses={selectGlasses}
				selectSizeHandler={(model, key) => {
					if (store?.state?.otherSizes) {
						set(p => ({
							...p,
							selectedSize: store?.state?.otherSizes[model],
							selectedSizeKey: key
						}))
					}
				}}
			/>

			<SRefresh>
				<Button icon={icons.faceScan} onClick={() => refresh()}>Rescan</Button>
			</SRefresh>

			{shouldShowLoading() && <Loading small={state.smallLoading} />}
		</SLayout>
	)
}
